import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const AppRefundPolicy = () => {
  // Check for utm=mobile query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = urlParams.get('utm') === 'mobile';

  const refundContent = `
**Effective Date:** 05/Oct/2025  
**Last Updated:** 05/Oct/2025  

## 1. Introduction & Scope

This Refund Policy applies to all purchases made through the MetroRahi mobile application ("App") developed by Resiara Software Pvt. Ltd. ("Company," "we," "our," or "us").

**Important:** MetroRahi uses Apple App Store and Google Play Store for all subscription and in-app purchases. All refund requests must be processed through the respective app store platforms.

---

## 2. Subscription Management

### 2.1 Platform-Based Subscriptions
MetroRahi offers premium features through subscription plans that are managed entirely by:

- **Apple App Store** (for iOS users)
- **Google Play Store** (for Android users)

### 2.2 No Direct Payment Processing
- We do not process payments directly
- We do not store credit card information
- All transactions are handled securely by Apple and Google
- We never have access to your payment details

---

## 3. Refund Process

### 3.1 Apple App Store Refunds
For iOS users, refund requests must be submitted through Apple:

**How to Request a Refund:**
1. Visit [reportaproblem.apple.com](https://reportaproblem.apple.com)
2. Sign in with your Apple ID
3. Find your MetroRahi purchase
4. Select "Request a refund"
5. Choose your reason and submit

**Apple's Refund Policy:**
- Refunds are typically processed within 24-48 hours
- Apple handles all refund decisions
- Refunds are subject to Apple's terms and conditions

### 3.2 Google Play Store Refunds
For Android users, refund requests must be submitted through Google:

**How to Request a Refund:**
1. Visit [play.google.com/store/account](https://play.google.com/store/account)
2. Go to "Order history"
3. Find your MetroRahi purchase
4. Click "Request a refund"
5. Select your reason and submit

**Google's Refund Policy:**
- Refunds are typically processed within 24-48 hours
- Google handles all refund decisions
- Refunds are subject to Google's terms and conditions

---

## 4. Refund Eligibility

### 4.1 General Eligibility
Refunds are generally available for:
- Accidental purchases
- Duplicate subscriptions
- Technical issues preventing app usage
- Unauthorized purchases
- Subscription cancellations within the refund window

### 4.2 Refund Windows
- **Apple App Store:** Up to 90 days from purchase
- **Google Play Store:** Up to 48 hours for most purchases, longer for subscriptions

### 4.3 Non-Refundable Items
- Used premium features beyond trial period
- Purchases made more than the refund window ago
- Refunds already processed for the same purchase

---

## 5. Subscription Cancellation

### 5.1 How to Cancel Subscriptions
**Apple App Store:**
1. Go to Settings > [Your Name] > Subscriptions
2. Find MetroRahi subscription
3. Tap "Cancel Subscription"

**Google Play Store:**
1. Open Google Play Store
2. Go to Subscriptions
3. Find MetroRahi subscription
4. Tap "Cancel"

### 5.2 Cancellation Effects
- Cancellation stops future billing
- Access continues until the end of current billing period
- No refund for unused time (unless requested through app store)

---

## 6. Our Role in Refunds

### 6.1 Limited Involvement
We cannot:
- Process refunds directly
- Override app store decisions
- Provide refunds outside of app store policies
- Guarantee refund approval

### 6.2 What We Can Do
We can:
- Provide purchase receipts and transaction IDs
- Assist with technical issues
- Help with subscription management
- Escalate issues to app stores when appropriate

---

## 7. Contact Information

### 7.1 For Refund Support
If you need assistance with refund requests:

📧 **Email:** support.metrorahi@resiara.com  
🏢 **Company:** Resiara Software Pvt. Ltd., Jaipur, Rajasthan, India

### 7.2 What to Include
When contacting us about refunds, please provide:
- Your app store account email
- Purchase date and amount
- Transaction ID (if available)
- Reason for refund request
- Screenshots of error messages (if applicable)

---

## 8. Dispute Resolution

### 8.1 App Store Disputes
If you disagree with an app store's refund decision:
- **Apple:** Contact Apple Support directly
- **Google:** Use Google Play's dispute resolution process

### 8.2 Our Commitment
We are committed to:
- Providing accurate information about refund processes
- Assisting with technical issues that may affect refunds
- Maintaining transparency about our refund limitations

---

## 9. Policy Updates

### 9.1 Changes to This Policy
We may update this Refund Policy to reflect:
- Changes in app store policies
- New subscription features
- Legal requirements

### 9.2 Notification of Changes
Users will be notified of significant changes through:
- In-app notifications
- App updates
- Email notifications (if applicable)

---

## 10. Legal Disclaimer

### 10.1 App Store Responsibility
All refund decisions are made by Apple and Google according to their respective policies. We are not responsible for:
- App store refund decisions
- Processing delays
- Refund denials
- Currency conversion rates

### 10.2 Limitation of Liability
Our liability for refund-related issues is limited to providing accurate information and reasonable assistance within our capabilities.

---

**By using MetroRahi, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.**

**Last Updated:** 05/Oct/2025

**Version:** 1.0`;

  return (
    <div className="policy-page app-policy">
      <div className="policy-container">
        <div className="policy-document">
          {/* Header */}
          <motion.div
            className="policy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {!isMobile && (
              <Link to="/" className="back-button">
                <ArrowLeft className="back-icon" />
                <span>Back</span>
              </Link>
            )}
            
            <div className="policy-title-section">
              <h1>Refund Policy</h1>
              <p>MetroRahi Mobile App Refund Policy</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="policy-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="policy-text">
              {refundContent.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <h1 key={index}>{line.substring(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={index}>{line.substring(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={index}>{line.substring(4)}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="bold-text">{line.substring(2, line.length - 2)}</p>;
                } else if (line.startsWith('- ')) {
                  return <li key={index}>{line.substring(2)}</li>;
                } else if (line.trim() === '---') {
                  return <hr key={index} className="section-divider" />;
                } else if (line.trim() === '') {
                  return <br key={index} />;
                } else {
                  return <p key={index}>{line}</p>;
                }
              })}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="policy-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="company-info">
              <p>Brought to you by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link">Resiara Software Pvt. Ltd.</a></p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AppRefundPolicy;
